/**
 * Web twin of `OnboardingPreviewBar`: a compact floating dock pinned to the
 * bottom of the viewport, matching the `/cv` bar (`CVBottomBar.web.tsx`) —
 * same gradient, border, shadow and 16px radius. Edit, Theme and the primary
 * "Continue" CTA all sit on one line; export lives on the CV screen,
 * not here.
 */
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { CV_BAR_ICON_COLOR, CV_BAR_ICON_PATHS } from '../icons/CvBottomBarIcons';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';

const BAR_BORDER_LIGHT = '#E9E9E9';
const BAR_BORDER_DARK = 'rgba(255,255,255,0.08)';

// The same 4-layer drop shadow the /cv dock uses (Figma 3147:710).
const BAR_SHADOW =
  '0px 7px 7px rgba(0,0,0,0.26), 0px 26px 13px rgba(0,0,0,0.04), 0px 59px 17.5px rgba(0,0,0,0.02)';
const DARK_BAR_SHADOW =
  '0px 14px 34px rgba(0,0,0,0.62), 0px 2px 8px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,255,255,0.04)';

/** The same 18px outline glyphs the native dock draws, as inline SVG. */
function DockIcon({ paths, color }: { paths: readonly string[]; color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none" aria-hidden>
      {paths.map((d) => (
        <path
          key={d}
          d={d}
          stroke={color}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

function BarButton({
  icon,
  label,
  labelColor,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  labelColor: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...buttonStyle,
        backgroundColor: hovered && !disabled ? 'var(--hover)' : 'transparent',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {icon}
      <span style={{ ...buttonLabelStyle, color: labelColor }}>{label}</span>
    </button>
  );
}

interface Props {
  onEdit: () => void;
  onTheme: () => void;
  onContinue: () => void;
  isBusy?: boolean;
}

export function OnboardingPreviewBar({
  onEdit,
  onTheme,
  onContinue,
  isBusy = false,
}: Props) {
  const { t } = useTranslation('auth');
  const { t: tSeeker } = useTranslation('seeker');
  const { theme, isDark } = useTheme();
  const { isRTL } = useLocale();
  const [ctaHovered, setCtaHovered] = useState(false);
  const iconColor = isDark ? theme.text : CV_BAR_ICON_COLOR;

  return (
    <div style={containerStyle}>
      <div
        style={{
          ...barStyle,
          borderColor: isDark ? BAR_BORDER_DARK : BAR_BORDER_LIGHT,
          background: isDark
            ? 'linear-gradient(to bottom, color-mix(in srgb, var(--menu-bg) 92%, #fff 8%) 0%, var(--menu-bg) 100%)'
            : 'linear-gradient(180deg, #F3F3F3 0%, #FFFFFF 60.5%)',
          boxShadow: isDark ? DARK_BAR_SHADOW : BAR_SHADOW,
        }}
      >
        <BarButton
          icon={<DockIcon paths={CV_BAR_ICON_PATHS.edit} color={iconColor} />}
          label={tSeeker('cv.bottomBar.edit', { defaultValue: 'Edit' })}
          labelColor={iconColor}
          onClick={onEdit}
          disabled={isBusy}
        />
        <BarButton
          icon={<DockIcon paths={CV_BAR_ICON_PATHS.theme} color={iconColor} />}
          label={tSeeker('cv.bottomBar.theme', { defaultValue: 'Theme' })}
          labelColor={iconColor}
          onClick={onTheme}
          disabled={isBusy}
        />

        <button
          type="button"
          onClick={onContinue}
          disabled={isBusy}
          onMouseEnter={() => setCtaHovered(true)}
          onMouseLeave={() => setCtaHovered(false)}
          style={{
            ...ctaStyle,
            background: 'var(--text-primary)',
            color: 'var(--bg)',
            opacity: isBusy ? 0.4 : ctaHovered ? 0.88 : 1,
            cursor: isBusy ? 'default' : 'pointer',
          }}
        >
          <span style={ctaLabelStyle}>
            {t('onboarding.complete.continueFromPreview', {
              defaultValue: 'Continue',
            })}
          </span>
          <MaterialSymbol
            name={isRTL ? 'arrow_back' : 'arrow_forward'}
            size={18}
          />
        </button>
      </div>
    </div>
  );
}

const containerStyle: CSSProperties = {
  position: 'fixed',
  bottom: 'max(20px, env(safe-area-inset-bottom))',
  insetInline: 0,
  zIndex: 50,
  display: 'flex',
  justifyContent: 'center',
  // The dock floats over the document; only the bar itself takes clicks so the
  // CV stays scrollable around it.
  pointerEvents: 'none',
  paddingInline: 16,
  boxSizing: 'border-box',
};

const barStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  height: 60,
  maxWidth: '100%',
  borderRadius: 16,
  borderWidth: 1,
  borderStyle: 'solid',
  padding: '4px 6px',
  boxSizing: 'border-box',
  pointerEvents: 'auto',
  overflowX: 'auto',
  scrollbarWidth: 'none',
};

const buttonStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
  minWidth: 40,
  height: 48,
  paddingInline: 12,
  border: 0,
  borderRadius: 12,
  background: 'transparent',
  fontFamily: 'inherit',
  flexShrink: 0,
  transition: 'background-color 150ms, opacity 150ms',
};

const buttonLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '15px',
  letterSpacing: -0.12,
  whiteSpace: 'nowrap',
};

const ctaStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  height: 48,
  paddingInline: 'clamp(12px, 3vw, 18px)',
  marginInlineStart: 4,
  border: 0,
  borderRadius: 999,
  fontFamily: 'inherit',
  flexShrink: 0,
  transition: 'opacity 150ms',
};

const ctaLabelStyle: CSSProperties = {
  fontSize: 'clamp(13px, 3.5vw, 15px)',
  fontWeight: 500,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
};
